import type React from 'react';
import { useMemo } from 'react';
import { colors } from '../theme/colors';

interface CellStyleProps {
  isSelected: boolean;
  isHovered: boolean;
  isCurrentMonth: boolean;
  isWeekend: boolean;
  isToday: boolean;
  isApproved: boolean;
  isDraft: boolean;
  isNotLogged: boolean;
  isHoliday: boolean;
  isPast: boolean;
  actualHours: number;
  projectHours: number;
  isAbsence: boolean;
  isVacation: boolean;
  isBridge: boolean;
}

export const useCalendarStyling = ({
  isSelected,
  isHovered,
  isCurrentMonth,
  isWeekend,
  isToday,
  isApproved,
  isDraft,
  isNotLogged,
  isHoliday,
  isPast,
  actualHours,
  projectHours,
  isAbsence,
  isVacation,
  isBridge,
}: CellStyleProps) => {
  const cellStyles = useMemo(() => {
    // Base styles
    let backgroundColor: string = 'transparent';
    let color: string = colors.secondary.text;
    let opacity = 1;
    let cursor = isCurrentMonth && !isWeekend ? 'pointer' : 'not-allowed';
    let border = '1px solid transparent';

    // Determine cell background color based on priority
    // Priority 1: Holiday - gray pastel
    if (isHoliday && isCurrentMonth) {
      backgroundColor = colors.pastel.gray;
      color = colors.neutral.grey[400];
      opacity = 0.7;
      cursor = 'not-allowed';
    }
    // Priority 2: Weekend - no special background, just faded
    else if (isWeekend) {
      backgroundColor = 'transparent';
      opacity = 0.5;
      cursor = 'not-allowed';
    }
    // Priority 3: Absence/Vacation/Bridge colors (for internal projects) - takes priority over regular status
    else if (isCurrentMonth && !isWeekend) {
      if (isAbsence) {
        backgroundColor = colors.pastel.brown;
        color = colors.secondary.text;
        cursor = 'pointer';
      } else if (isVacation) {
        backgroundColor = colors.pastel.brownDark;
        color = colors.secondary.text;
        cursor = 'pointer';
      } else if (isBridge) {
        backgroundColor = '#faa1c9';
        color = colors.secondary.text;
        cursor = 'pointer';
      } else {
        // Priority 4: Regular status colors (yellow/red/green) - only if not absence/vacation
        const hasLoggedHours = actualHours > 0;
        const TOLERANCE = 0.01;
        const hoursMatch = hasLoggedHours && Math.abs(actualHours - projectHours) < TOLERANCE;

        if (isNotLogged || !hasLoggedHours) {
          backgroundColor = colors.pastel.yellow;
          color = colors.secondary.text;
        } else if (hoursMatch) {
          backgroundColor = colors.pastel.green;
          color = colors.secondary.text;
        } else {
          backgroundColor = colors.pastel.red;
          color = colors.secondary.text;
        }
        cursor = 'pointer';
      }
    }

    // Border priority: Today > Selected > Hover (use longhand to avoid conflict with borderLeft)
    if (isSelected && isCurrentMonth && !isHoliday) {
      border = `2px solid ${colors.accent.blue}`;
    } else if (isHovered && isCurrentMonth && !isWeekend && !isHoliday) {
      border = `2px solid ${colors.accent.blue}80`;
    } else if (isToday && isCurrentMonth && !isHoliday) {
      border = `2px solid ${colors.accent.blue}50`;
    }


    return {
      margin: '0 auto',
      aspectRatio: '1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px',
      fontSize: '0.85rem',
      backgroundColor,
      color,
      fontWeight: isToday ? '600' : '400',
      width: isWeekend ? '2rem' : '2.5rem',
      opacity,
      cursor,
      transition: 'all 0.2s ease',
      border: border,
      transform:
        isHovered && isCurrentMonth && !isWeekend && !isHoliday ? 'scale(1.05)' : 'scale(1)',
    };
  }, [
    isSelected,
    isHovered,
    isCurrentMonth,
    isWeekend,
    isToday,
    isNotLogged,
    isHoliday,
    actualHours,
    projectHours,
    isAbsence,
    isVacation,
    isBridge,
  ]);

  const statusIndicatorStyles = useMemo(
    (): React.CSSProperties => ({
      // Status indicator is now handled by cell background
      // This is kept for compatibility but hidden
      display: 'none',
    }),
    []
  );

  const todayIndicatorStyles = useMemo(
    (): React.CSSProperties => ({
      position: 'absolute',
      width: '1.5rem',
      height: '2px',
      backgroundColor: colors.accent.blue,
      bottom: '0.2rem',
      zIndex: 3,
    }),
    []
  );

  const textStyles = useMemo((): React.CSSProperties => {
    let textColor: string = colors.secondary.text;
    let fontWeight: number | string = '400';

    // Holiday text styling
    if (isHoliday) {
      textColor = colors.neutral.grey[400];
      fontWeight = '400';
    }
    // Past days (non-holiday) use darker text to differentiate
    else if (isPast && !isToday) {
      textColor = colors.neutral.grey[700];
      fontWeight = '500';
    }
    // Today gets bold text
    else if (isToday) {
      fontWeight = '600';
      textColor = colors.secondary.text;
    }
    // Weekend text
    else if (isWeekend) {
      textColor = colors.secondary.text;
      fontWeight = '400';
    }
    // Regular weekday text
    else {
      textColor = colors.secondary.text;
      fontWeight = '500';
    }

    return {
      position: 'relative',
      zIndex: 2,
      fontWeight,
      color: textColor,
    };
  }, [isWeekend, isToday, isHoliday, isPast]);

  return {
    cellStyles,
    statusIndicatorStyles,
    todayIndicatorStyles,
    textStyles,
  };
};
